// The Act now card's own windows (walkthrough, 30 Oct 2026; server:
// services/actNow.service.js):
//   NudgeWindow     — who was invited to a round and has not taken it; send
//                     each their invitation again (POST /dashboard/act-now/nudge)
//   ActNowSettings  — which items show, after how many days to nudge, and the
//                     daily email to the hiring administrators
//   AiSuggestions   — the AI's next steps, from the figures only
//   SetupNotReady   — the published roles whose written tests or AI interview
//                     briefs are not ready, each with a link to its pipeline
//                     (@story HIR-HR21; the readiness grid, 30 Oct 2026)
// Each is also an MCP tool (act_now, nudge_not_taken, act_now_settings,
// act_now_suggestions).
import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, StatusPill } from '../../ds';

const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' };

export function SetupNotReady({ roles, onOpen }) {
  if (!roles || !roles.length) return <p style={muted}>Every published role's tests and interview briefs are ready.</p>;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ ...muted, margin: 0 }}>Set up each stage and have it approved before inviting candidates to it. The role's pipeline shows every stage and can draft the missing interview briefs.</p>
      {roles.map((r) => (
        <div key={r.jobId} style={{ display: 'grid', gap: 6, borderBottom: '1px solid var(--border)', paddingBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ flex: 1 }}>{r.title}</strong>
            <Button size="sm" variant="secondary" onClick={() => onOpen(r.jobId)}>Open its pipeline</Button>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, ...muted }}>
            {r.stages.map((s) => <li key={s.label}>{s.label} ({s.what}): {s.state}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function NudgeWindow({ onDone }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/dashboard/act-now/not-taken')
      .then((r) => setList(r.data?.data || { days: 3, people: [] }))
      .catch(() => setErr('Could not read who has not taken their round.'));
  }, []);

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.post('/dashboard/act-now/nudge', {});
      setResult(r.data?.data || null);
      onDone?.();
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not send the nudges.');
    } finally { setBusy(false); }
  };

  if (err && !list) return <p style={{ color: 'var(--status-danger-text)' }}>{err}</p>;
  if (!list) return <p style={muted}>Reading who has not taken their round…</p>;
  if (result) {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        <StatusPill tone="success" dot={false}>Sent again to {result.nudged.length}</StatusPill>
        {result.notNudged.length > 0 && (
          <div>
            <p style={{ margin: '0 0 6px', fontWeight: 500 }}>Not sent ({result.notNudged.length})</p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>{result.notNudged.map((n, i) => <li key={i} style={muted}>{n.name}: {n.why}</li>)}</ul>
          </div>
        )}
      </div>
    );
  }
  if (!list.people.length) return <p style={muted}>Nobody invited {list.days}+ days ago is still to take their round.</p>;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0 }}>
        These candidates were invited to a round {list.days} or more days ago and have not taken it.
        A nudge sends each their invitation again — a fresh link, by email and WhatsApp, through Messages.
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 280, overflowY: 'auto' }}>
        {list.people.map((p) => <li key={`${p.resumeId}-${p.stageId}`}>{p.name} — {p.stage} <span style={muted}>(invited {p.days} days ago)</span></li>)}
      </ul>
      {err && <p style={{ color: 'var(--status-danger-text)', margin: 0 }}>{err}</p>}
      <div><Button onClick={send} disabled={busy}>{busy ? 'Sending…' : `Nudge ${list.people.length}`}</Button></div>
    </div>
  );
}

export function ActNowSettings({ items, settings, onSaved }) {
  const [hidden, setHidden] = useState(new Set(settings?.hidden || []));
  const [days, setDays] = useState(settings?.nudgeAfterDays || 3);
  const [alertsOn, setAlertsOn] = useState(Boolean(settings?.alertsOn));
  const [hour, setHour] = useState(settings?.alertHourIst || 9);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggle = (k) => setHidden((h) => { const n = new Set(h); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await api.put('/dashboard/act-now/settings', { hidden: [...hidden], nudgeAfterDays: Number(days), alertsOn, alertHourIst: Number(hour) });
      setMsg({ tone: 'success', text: 'Saved.' });
      onSaved?.();
    } catch (e) {
      setMsg({ tone: 'danger', text: e.response?.data?.message || 'Could not save.' });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontWeight: 600, marginBottom: 6 }}>Show on Act now</legend>
        {items.map((i) => (
          <label key={i.key} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
            <input type="checkbox" checked={!hidden.has(i.key)} onChange={() => toggle(i.key)} /> {i.label}
          </label>
        ))}
      </fieldset>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        List invited candidates to nudge after
        <input type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value)} style={{ width: 64 }} className="eq-native-select" aria-label="Days" />
        days
      </label>
      <div style={{ display: 'grid', gap: 6 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={alertsOn} onChange={(e) => setAlertsOn(e.target.checked)} />
          Email the school's hiring administrators each morning when something needs acting on
        </label>
        {alertsOn && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 24 }}>
            at
            <select value={hour} onChange={(e) => setHour(e.target.value)} className="eq-native-select" aria-label="Hour">
              {Array.from({ length: 15 }, (_, i) => i + 6).map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
            India time
          </label>
        )}
        <span style={muted}>By email, through Messages, to the people who hold Hiring admin at this school. WhatsApp needs an approved template first.</span>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        {msg && <StatusPill tone={msg.tone} dot={false}>{msg.text}</StatusPill>}
      </div>
    </div>
  );
}

export function AiSuggestions() {
  const [state, setState] = useState({ busy: true, list: [], err: '' });
  useEffect(() => {
    api.post('/dashboard/act-now/suggest', {})
      .then((r) => setState({ busy: false, list: r.data?.data?.suggestions || [], err: '' }))
      .catch((e) => setState({ busy: false, list: [], err: e.response?.data?.message || 'The AI could not suggest anything just now.' }));
  }, []);
  if (state.busy) return <p style={muted}>Asking the AI what to do next…</p>;
  if (state.err) return <p style={{ color: 'var(--status-warning-text)' }}>{state.err}</p>;
  if (!state.list.length) return <p style={muted}>The AI had nothing to suggest.</p>;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 8 }}>
        {state.list.map((s, i) => <li key={i}><strong style={{ fontWeight: 600 }}>{s.step}</strong>{s.why ? <div style={muted}>{s.why}</div> : null}</li>)}
      </ol>
      <span style={muted}>From the dashboard's figures only. Advice — the team decides.</span>
    </div>
  );
}
